import { useTranslation } from 'react-i18next'
import { Tabs, Tab, Button, Divider } from '@mui/material'
import { forwardRef, useImperativeHandle, useContext, useEffect, useState, ChangeEvent, useRef } from 'react'

import Chip from '../common/chip'
import { PaymentTagType } from '../../lib'
import { UserContext } from '../../contexts'
import { TabPanel } from './background-research-novelty-form'
import ClarifyIcon from '../../assets/icons/clarify-icon.svg'
import { PaymentTicketsModule } from './payment-tickets-module'
import PaymentStatusIcon from '../../assets/icons/payment-status-icon.svg'
import { Payment, PaymentTag, SupportTicket, FormError } from '../../types'
import PaymentDecisionIcon from '../../assets/icons/payment-decision-icon.svg'
import { baseButtonBlueBg, /* baseButtonWarning, */ modalTabs } from '../../theme/custom-styles'
import { BackgroundAlertsFindings } from '../payment-with-alerts-details-management/background-alerts-findings'
import { ComplianceResolutionModule } from '../customer-company-details-management/compliance-resolution-module'
import { format } from 'date-fns'

export const PaymentBackgroundNovelties = forwardRef(
  (
    {
      errors,
      paymentData,
      paymentTicketsData,
      handleOpenRejectPaymentModal,
      handleDownloadAttachmentFile,
      handleSendCompliancePaymentRequest,
      handleSendCompliancePaymentResponse
    }: {
      errors: FormError
      handleOpenRejectPaymentModal?: () => void
      paymentTicketsData?: Array<SupportTicket>
      paymentData: Payment | Partial<Payment> | null
      handleDownloadAttachmentFile: (attachmentFilename: string) => void
      handleSendCompliancePaymentRequest: (requestToSend: string) => void
      handleSendCompliancePaymentResponse: (responseToSend: string, supportTicketAttachments: Array<{ file: File | null; fileType: string; filename: string; base64: string }>) => void
    },
    ref
  ) => {
    const { userSession } = useContext(UserContext)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const { t } = useTranslation(['payments-to-review-management', 'document-payments-details-management', 'common'])

    const [localErrors, setLocalErrors] = useState<FormError>({})
    const [requestToSend, setRequestToSend] = useState<string>('')
    const [selectedResultsTab, setSelectedResultsTab] = useState(0)
    const [responseToSend, setResponseToSend] = useState<string>('')
    const [drawerOpened, setDrawerOpened] = useState<boolean>(false)
    const [paymentDecision, setPaymentDecision] = useState<string>('pending')
    const [paymentDecisionText, setPaymentDecisionText] = useState<string>('')
    const [paymentTagsToResolve, setPaymentTagsToResolve] = useState<Array<PaymentTag>>([])
    const [backgroundResearchData, setBackgroundResearchData] = useState<{
      riskLevel: number
      noveltiesByNameCount: number
      noveltiesByDocumentNumberCount: number
      backgroundResearhNoveltiesByName: Array<any>
      backgroundResearhNoveltiesByDocumentNumber: Array<any>
    }>({ riskLevel: 0, noveltiesByNameCount: 0, noveltiesByDocumentNumberCount: 0, backgroundResearhNoveltiesByName: [], backgroundResearhNoveltiesByDocumentNumber: [] })
    const [supportTicketAttachments, setSupportTicketAttachments] = useState<Array<{ file: File | null; fileType: string; filename: string; base64: string }>>([])

    useImperativeHandle(
      ref,
      () => {
        return {
          handleResetToInitState() {
            setRequestToSend('')
            setResponseToSend('')
            setSupportTicketAttachments([])
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    )

    useEffect(() => {
      if (paymentData) {
        const backgroundResearchNovelty = paymentData.novelties?.find((noveltyData) => noveltyData.type === 'background-research')

        if (backgroundResearchNovelty) {
          const riskLevel =
            Number(backgroundResearchNovelty.backgroundResearchRiskLevelByDocumentNumber) > Number(backgroundResearchNovelty.backgroundResearchRiskLevelByName)
              ? Number(backgroundResearchNovelty.backgroundResearchRiskLevelByDocumentNumber)
              : Number(backgroundResearchNovelty.backgroundResearchRiskLevelByDocumentNumber) < Number(backgroundResearchNovelty.backgroundResearchRiskLevelByName)
                ? Number(backgroundResearchNovelty.backgroundResearchRiskLevelByName)
                : Number(backgroundResearchNovelty.backgroundResearchRiskLevelByDocumentNumber) === Number(backgroundResearchNovelty.backgroundResearchRiskLevelByName) &&
                  Number(backgroundResearchNovelty.backgroundResearchRiskLevelByDocumentNumber)

          setBackgroundResearchData({
            riskLevel: Number(riskLevel),
            backgroundResearhNoveltiesByName: backgroundResearchNovelty.backgroundResearhNoveltiesByName ? backgroundResearchNovelty.backgroundResearhNoveltiesByName : [],
            noveltiesByNameCount: backgroundResearchNovelty.backgroundResearhNoveltiesByName ? backgroundResearchNovelty.backgroundResearhNoveltiesByName.length : 0,
            noveltiesByDocumentNumberCount: backgroundResearchNovelty.backgroundResearhNoveltiesByDocumentNumber ? backgroundResearchNovelty.backgroundResearhNoveltiesByDocumentNumber.length : 0,
            backgroundResearhNoveltiesByDocumentNumber: backgroundResearchNovelty.backgroundResearhNoveltiesByDocumentNumber ? backgroundResearchNovelty.backgroundResearhNoveltiesByDocumentNumber : []
          })

          if (backgroundResearchNovelty.resolved === false && backgroundResearchNovelty.rejected === false) {
            setPaymentDecision('pending')
          } else {
            setPaymentDecision(backgroundResearchNovelty.resolved ? 'accepted' : 'rejected')
            setPaymentDecisionText(backgroundResearchNovelty.reasonOfActionOfDecision ?? t('noveltiesTable.withoutAReason'))
          }
        }

        if (!backgroundResearchNovelty && paymentData.status === 'rejected') {
          setPaymentDecision('rejected')
          setPaymentDecisionText(paymentData.description ?? t('noveltiesTable.withoutAReason'))
        }

        if (paymentData.status === 'returned') {
          setPaymentDecisionText(paymentData.description.toUpperCase())
        }

        if (!backgroundResearchNovelty && paymentData.novelties?.length === 0 && paymentData.status !== 'deleted') {
          setPaymentDecision('accepted')
        }

        // if (!backgroundResearchNovelty && paymentData.novelties?.length === 0 && paymentData.status === 'deleted') {
        //   setPaymentDecision('deleted')
        // }

        if (paymentData.status === 'deleted') {
          setPaymentDecision('deleted')
        }

        if (paymentData.status === 'waiting-for-resolution' && Array.isArray(paymentData.tags) && paymentData.tags.length > 0) {
          const paymentTagsToResolveToSet = []
          const currentPaymentTags = [...paymentData.tags]

          for (let i = 0; i < currentPaymentTags.length; i++) {
            const tagData = currentPaymentTags[i]

            if (
              tagData.type === PaymentTagType.BLACK_LISTED ||
              tagData.type === PaymentTagType.TO_CONFIRM_ORIGIN_OF_FUNDS_DAILY ||
              tagData.type === PaymentTagType.TO_CONFIRM_ORIGIN_OF_FUNDS_MONTHLY ||
              tagData.type === PaymentTagType.TO_CONFIRM_ORIGIN_OF_FUNDS_WEEKLY ||
              tagData.type === PaymentTagType.TO_CONFIRM_PREVIOUS_PAYMENT_WAITING_FOR_RESOLUTION
            ) {
              paymentTagsToResolveToSet.push(tagData)
            }
          }

          setPaymentTagsToResolve(paymentTagsToResolveToSet)
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentData])

    const handleChange = (_: unknown, selectedTabId: number) => {
      setSelectedResultsTab(selectedTabId)
    }

    const tabProps = (index: number) => {
      return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`
      }
    }

    const handleAddAttachmentToSupportTicket = (event: ChangeEvent<HTMLInputElement>) => {
      setLocalErrors({})

      if (event.target.files?.[0]) {
        const fileType = event.target.files[0].type
        const fileSize = event.target.files[0].size / 1024 / 1024

        if (fileSize > 5) {
          setLocalErrors({
            attachmentFileError: t('complianceProcess.errors.attachmentFileError')
          })

          return
        }

        if (fileType !== 'application/pdf' && !fileType.startsWith('image/')) {
          setLocalErrors({
            attachmentFileError: t('complianceProcess.errors.attachmentFileTypeError')
          })

          return
        }

        const reader = new FileReader()
        reader.readAsDataURL(event.target.files[0])
        reader.onload = () => {
          const supportTicketAttachmentsUpdated = [...supportTicketAttachments]
          supportTicketAttachmentsUpdated.push({
            fileType,
            base64: reader.result as string,
            file: event.target.files ? event.target.files[0] : null,
            filename: event.target.files ? event.target.files[0].name : ''
          })

          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }

          setSupportTicketAttachments([...supportTicketAttachmentsUpdated])
        }
        reader.onerror = function (error) {
          console.log('Error: ', error)
        }
      }
    }

    const handleRemoveSupportTicketAttachment = (indexToRemove: number) => {
      const supportTicketAttachmentsUpdated = [...supportTicketAttachments]
      supportTicketAttachmentsUpdated.splice(indexToRemove, 1)

      setSupportTicketAttachments([...supportTicketAttachmentsUpdated])
    }

    return (
      <>
        <div className="table-row-container">
          <div className="left-content">
            <div className="icon-container">
              <img
                src={PaymentStatusIcon}
                alt="table-row-icon"
              />
            </div>
            <div className="table-row-content-subtitle">{t('noveltiesTable.status')}</div>
          </div>
          <div
            className="right-content"
            style={{ justifyContent: 'space-between' }}
          >
            <Chip
              status={paymentData?.status ?? 'reviewed'}
              label={
                paymentData?.status === 'reviewed'
                  ? t('paymentStatus.reviewed', { ns: 'document-payments-details-management' })
                  : paymentData?.status?.indexOf('error') !== -1
                    ? t('paymentStatus.error', { ns: 'document-payments-details-management' })
                    : paymentData?.status === 'paid'
                      ? t('paymentStatus.paid', { ns: 'document-payments-details-management' })
                      : paymentData?.status === 'processing'
                        ? t('paymentStatus.processing', { ns: 'document-payments-details-management' })
                        : paymentData?.status === 'rejected'
                          ? t('paymentStatus.rejected', { ns: 'document-payments-details-management' })
                          : paymentData?.status === 'deleted'
                            ? t('paymentStatus.deleted', { ns: 'document-payments-details-management' })
                            : paymentData?.status === 'returned'
                              ? t('paymentStatus.returned', { ns: 'document-payments-details-management' })
                              : paymentData?.status === 'waiting-for-resolution'
                                ? t('paymentStatus.waitingForResolution', { ns: 'document-payments-details-management' })
                                : paymentData?.status === 'validated'
                                  ? t('paymentStatus.validated', { ns: 'document-payments-details-management' })
                                  : t('paymentStatus.forReview', { ns: 'document-payments-details-management' })
              }
            />
            {userSession && userSession.belongsTo === 'holdings' && String(userSession.subRole) === 'compliance-specialist' && (
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                {paymentData && 'complianceResolutionData' in paymentData && (
                  <>
                    {paymentData.complianceResolutionData?.approved && (
                      <div style={{ color: 'rgb(115, 115, 115)', lineHeight: '150%', fontSize: '16px' }}>
                        {t('paymentApprovedByCompliance', {
                          approvedByName: String(paymentData.complianceResolutionData?.approvedBy.name).toUpperCase(),
                          approvedDate: format(paymentData.complianceResolutionData?.decisionDate, 'dd/MM/yyyy | HH:mm:ss')
                        })}
                      </div>
                    )}
                    {paymentData.complianceResolutionData?.rejected && (
                      <div style={{ color: 'rgb(115, 115, 115)', lineHeight: '150%', fontSize: '16px' }}>
                        {t('paymentRejectedByCompliance', {
                          rejectedByName: String(paymentData.complianceResolutionData?.rejectedBy.name).toUpperCase(),
                          rejectedDate: format(paymentData.complianceResolutionData?.decisionDate, 'dd/MM/yyyy | HH:mm:ss')
                        })}
                      </div>
                    )}
                  </>
                )}
                <Button
                  variant="text"
                  sx={baseButtonBlueBg}
                  onClick={() => setDrawerOpened(true)}
                >
                  {t('viewProcessComments')}
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="table-row-container compliance-warning-table-row-container">
          <div className="left-content">
            <div className="icon-container">
              <img
                src={PaymentDecisionIcon}
                alt="table-row-icon"
              />
            </div>
            {userSession && userSession.role.indexOf('-client') > -1 ? (
              <div className="table-row-content-subtitle">{t('noveltiesTable.paymentDecision')}</div>
            ) : (
              <div className="table-row-content-subtitle">{t('noveltiesTable.clientPaymentDecision')}</div>
            )}
          </div>
          <div
            style={{ gap: '15px' }}
            className="right-content"
          >
            <div className="payment-decision-container">
              <Chip
                status={paymentDecision ?? 'pending'}
                label={
                  paymentDecision === 'accepted'
                    ? t('paymentStatus.accepted', { ns: 'document-payments-details-management' })
                    : paymentDecision === 'pending'
                      ? t('paymentStatus.pending', { ns: 'document-payments-details-management' })
                      : paymentDecision === 'deleted'
                        ? t('paymentStatus.deleted', { ns: 'document-payments-details-management' })
                        : t('paymentStatus.rejected', { ns: 'document-payments-details-management' })
                }
              />
              {paymentDecisionText && <div style={{ color: '#737373' }}>{paymentDecisionText.toUpperCase()}</div>}
              {/* paymentDecision === 'pending' ? (
                <>
                  {paymentData?.status === 'waiting-for-resolution' && userSession && userSession.belongsTo === 'holdings' && String(userSession.subRole) === 'compliance-specialist' && (
                    <div style={{ display: 'flex', gap: '15px' }}>
                      <Button
                        variant="text"
                        sx={baseButtonBlueBg}
                        onClick={() => console.log('entro')}
                      >
                        {t('sendPayment')}
                      </Button>
                      <Button
                        variant="contained"
                        sx={baseButtonWarning}
                        onClick={handleOpenRejectPaymentModal}
                      >
                        {t('rejectPayment')}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ color: '#737373' }}>{paymentDecisionText.toUpperCase()}</div>
              ) */}
            </div>
          </div>
        </div>
        {userSession && userSession.role.indexOf('-client') > -1 && String(paymentData?.status) === 'waiting-for-resolution' && paymentTicketsData?.length === 0 && (
          <div className="comments-warning-container">{t('complianceProcess.commentsWarning')}</div>
        )}
        {/* userSession && userSession.role.indexOf('-client') > -1 && String(paymentData?.status) === 'waiting-for-resolution' && Number(paymentTicketsData?.length) > 0 && (
          <PaymentTicketsModule
            errors={errors}
            paymentData={paymentData}
            localErrors={localErrors}
            fileInputRef={fileInputRef}
            requestToSend={requestToSend}
            responseToSend={responseToSend}
            setRequestToSend={setRequestToSend}
            setResponseToSend={setResponseToSend}
            paymentTicketsData={paymentTicketsData}
            paymentTagsToResolve={paymentTagsToResolve}
            supportTicketAttachments={supportTicketAttachments}
            handleDownloadAttachmentFile={handleDownloadAttachmentFile}
            handleSendCompliancePaymentRequest={handleSendCompliancePaymentRequest}
            handleAddAttachmentToSupportTicket={handleAddAttachmentToSupportTicket}
            handleRemoveSupportTicketAttachment={handleRemoveSupportTicketAttachment}
            handleSendCompliancePaymentResponse={handleSendCompliancePaymentResponse}
          />
        ) */}
        {(backgroundResearchData.noveltiesByDocumentNumberCount > 0 || backgroundResearchData.noveltiesByNameCount > 0) && (
          <>
            <div
              className="table-row-container"
              style={{ borderBottom: 'none' }}
            >
              <div className="left-content">
                <div className="icon-container">
                  <img
                    src={ClarifyIcon}
                    alt="table-row-icon"
                  />
                </div>
                <div className="table-row-content-subtitle">{t('noveltiesTable.findings')}</div>
              </div>
            </div>
            <Tabs
              centered
              sx={modalTabs}
              variant="fullWidth"
              onChange={handleChange}
              value={selectedResultsTab}
              aria-label="basic tabs example"
              TabIndicatorProps={{
                sx: {
                  backgroundColor: 'rgb(46, 211, 183)'
                }
              }}
            >
              <Tab
                label={`${t('resolveNoveltyModal.backgroundResearch.resultsById')} (${backgroundResearchData.noveltiesByDocumentNumberCount})`}
                {...tabProps(0)}
              />
              <Tab
                label={`${t('resolveNoveltyModal.backgroundResearch.resultsByName')} (${backgroundResearchData.noveltiesByNameCount})`}
                {...tabProps(1)}
              />
            </Tabs>
            <TabPanel
              index={0}
              maxHeight="800px"
              value={selectedResultsTab}
            >
              {backgroundResearchData.noveltiesByDocumentNumberCount === 0 ? (
                <div style={{ padding: '20px', boxSizing: 'border-box', color: '#737373', lineHeight: '150%' }}>{t('resolveNoveltyModal.backgroundResearch.noResults')}</div>
              ) : backgroundResearchData.noveltiesByDocumentNumberCount > 0 && userSession && userSession.belongsTo !== 'holdings' ? (
                <div
                  style={{ flexDirection: 'column' }}
                  className="identification-document-container"
                >
                  <div
                    className="subtitle"
                    style={{ color: '#F59E0B', lineHeight: '150%' }}
                  >
                    {t('resolveNoveltyModal.backgroundResearch.detailsTitle')}
                  </div>
                  {backgroundResearchData.backgroundResearhNoveltiesByDocumentNumber.map((backgroundResearhNovelty: any, index: number) => {
                    return (
                      <div
                        key={index}
                        style={{ padding: '15px', boxSizing: 'border-box', borderBottom: '1px dashed #F7DDBA' }}
                        className="identification-document-beneficiary-container"
                      >
                        {backgroundResearhNovelty.nombres && (
                          <div className="identification-document-row-item">
                            <div className="left-content">
                              <span>{t('resolveNoveltyModal.backgroundResearch.fullNameFound')}</span>
                              <div>{backgroundResearhNovelty.nombres}</div>
                            </div>
                            <div className="right-content">
                              <span>{t('resolveNoveltyModal.backgroundResearch.riskLevel')}</span>
                              <div>{backgroundResearhNovelty.riskLevel}</div>
                            </div>
                          </div>
                        )}
                        <div className="identification-document-row-item">
                          {backgroundResearhNovelty.codigoLista && (
                            <div className="left-content">
                              <span>{t('resolveNoveltyModal.backgroundResearch.listCode')}</span>
                              <div>{backgroundResearhNovelty.codigoLista}</div>
                            </div>
                          )}
                          {backgroundResearhNovelty.link && (
                            <div className="right-content">
                              <span>{t('resolveNoveltyModal.backgroundResearch.linkToVerify')}</span>
                              <a
                                target="_blank"
                                rel="noreferrer"
                                href={backgroundResearhNovelty.link}
                              >
                                {backgroundResearhNovelty.link}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                backgroundResearchData.noveltiesByDocumentNumberCount > 0 &&
                userSession &&
                userSession.belongsTo === 'holdings' && <BackgroundAlertsFindings alerts={backgroundResearchData.backgroundResearhNoveltiesByDocumentNumber} />
              )}
            </TabPanel>
            <TabPanel
              index={1}
              maxHeight="800px"
              value={selectedResultsTab}
            >
              {backgroundResearchData.noveltiesByNameCount === 0 ? (
                <div style={{ padding: '20px', boxSizing: 'border-box', color: '#737373', lineHeight: '150%' }}>{t('resolveNoveltyModal.backgroundResearch.noResults')}</div>
              ) : backgroundResearchData.noveltiesByNameCount > 0 && userSession && !('subRole' in userSession) && userSession.belongsTo !== 'holdings' ? (
                <div
                  style={{ flexDirection: 'column' }}
                  className="identification-document-container"
                >
                  <div
                    className="subtitle"
                    style={{ color: '#F59E0B', lineHeight: '150%' }}
                  >
                    {t('resolveNoveltyModal.backgroundResearch.detailsTitle')}
                  </div>
                  {backgroundResearchData.backgroundResearhNoveltiesByName.map((backgroundResearhNovelty: any, index: number) => {
                    return (
                      <div
                        key={index}
                        style={{ padding: '15px', boxSizing: 'border-box', borderBottom: '1px dashed #F7DDBA' }}
                        className="identification-document-beneficiary-container"
                      >
                        {backgroundResearhNovelty.nombres && (
                          <div className="identification-document-row-item">
                            <div className="left-content">
                              <span>{t('resolveNoveltyModal.backgroundResearch.fullNameFound')}</span>
                              <div>{backgroundResearhNovelty.nombres}</div>
                            </div>
                            <div className="right-content">
                              <span>{t('resolveNoveltyModal.backgroundResearch.riskLevel')}</span>
                              <div>{backgroundResearhNovelty.riskLevel}</div>
                            </div>
                          </div>
                        )}
                        <div className="identification-document-row-item">
                          {backgroundResearhNovelty.codigoLista && (
                            <div className="left-content">
                              <span>{t('resolveNoveltyModal.backgroundResearch.listCode')}</span>
                              <div>{backgroundResearhNovelty.codigoLista}</div>
                            </div>
                          )}
                          {backgroundResearhNovelty.link && (
                            <div className="right-content">
                              <span>{t('resolveNoveltyModal.backgroundResearch.linkToVerify')}</span>
                              <a
                                target="_blank"
                                rel="noreferrer"
                                href={backgroundResearhNovelty.link}
                              >
                                {backgroundResearhNovelty.link}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                backgroundResearchData.noveltiesByNameCount > 0 && userSession && userSession.belongsTo === 'holdings' && <BackgroundAlertsFindings alerts={backgroundResearchData.backgroundResearhNoveltiesByName} />
              )}
            </TabPanel>
          </>
        )}
        <Divider sx={{ margin: '40px 0px 10px' }} />
        {userSession &&
          ((userSession.belongsTo === 'holdings' && userSession.subRole === 'compliance-specialist') ||
            (userSession.belongsTo === String(paymentData?.belongsTo) && userSession.role.indexOf('-client') > -1 && paymentTicketsData && paymentTicketsData.length > 0)) && (
            <PaymentTicketsModule
              errors={errors}
              paymentData={paymentData}
              localErrors={localErrors}
              fileInputRef={fileInputRef}
              requestToSend={requestToSend}
              responseToSend={responseToSend}
              setRequestToSend={setRequestToSend}
              setResponseToSend={setResponseToSend}
              paymentTicketsData={paymentTicketsData}
              paymentTagsToResolve={paymentTagsToResolve}
              supportTicketAttachments={supportTicketAttachments}
              handleDownloadAttachmentFile={handleDownloadAttachmentFile}
              handleSendCompliancePaymentRequest={handleSendCompliancePaymentRequest}
              handleAddAttachmentToSupportTicket={handleAddAttachmentToSupportTicket}
              handleRemoveSupportTicketAttachment={handleRemoveSupportTicketAttachment}
              handleSendCompliancePaymentResponse={handleSendCompliancePaymentResponse}
            />
          )}
        {userSession && userSession.belongsTo === 'holdings' && (
          <ComplianceResolutionModule
            paymentData={paymentData}
            drawerOpened={drawerOpened}
            setDrawerOpened={setDrawerOpened}
            paymentTicketsData={paymentTicketsData}
            handleOpenRejectPaymentModal={handleOpenRejectPaymentModal}
          />
        )}
      </>
    )
  }
)
